import { useLocation, useNavigate } from 'react-router-dom';
import { PageContainer } from '../components/common/PageComponent.style';
import PageTop from '../components/pageTop/PageTop';
import * as S from './SignUpForm.style';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AgreeState, feedbackMessages, REGEX, termList } from './data';
import SignUpSerivce from '../../service/signUp/SignUp';
import styled, { css } from 'styled-components';

export function useTextField(
  validator?: (v: string) => string | null,
  required: boolean = true,
) {
  const [value, setValue] = useState('');
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const onChange = (v: string) => {
    setValue(v);
    // 값이 바뀌면 성공 해제
    setSuccess(false);
    setError(validator?.(v) ?? null);
  };

  const onBlur = () => {
    setTouched(true);
    if (required && !value) setError('required');
  };

  return {
    value,
    setValue,
    touched,
    setTouched,
    error,
    setError,
    success,
    setSuccess,
    onChange,
    onBlur,
  };
}

const emailList = [
  'naver.com',
  'hanmail.net',
  'daum.net',
  'gmail.com',
  'nate.com',
  'outlook.com',
  'icloud.com',
  '직접입력',
];

function SignUpForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const userType = location.state?.type || 'personal'; // 기본값 설정

  const username = useTextField((v) => {
    if (!v) return null;
    return REGEX.username.test(v) ? null : 'invalid';
  });

  const password = useTextField((v) => {
    if (!v) return null;
    // 8~16자 + 영문 + 숫자 + 특수문자 하나 이상
    const okLen = REGEX.passwordLength.test(v);
    const hasAlpha = /[A-Za-z]/.test(v);
    const hasNum = /[0-9]/.test(v);
    const hasSpecial = /[!@#$%^*&?]/.test(v);
    return okLen && hasAlpha && hasNum && hasSpecial ? null : 'invalid';
  });

  const passwordConfirm = useTextField();

  const name = useTextField((v) => {
    if (!v) return null;
    return REGEX.name.test(v) ? null : 'invalid';
  });

  const phone = useTextField((v) => {
    if (!v) return null;
    return REGEX.phone.test(v) ? null : 'invalid';
  });

  const phoneCode = useTextField();

  const emailLocal = useTextField(undefined, false);
  const emailCustomDomain = useTextField(
    (v) => (!v ? null : REGEX.emailDomain.test(v) ? null : 'invalid'),
    false,
  );

  const recommendCode = useTextField(undefined, false);

  // 채권보유사 회원가입
  const department = useTextField(undefined, false);
  const position = useTextField(undefined, false);

  const [agree, setAgree] = useState<AgreeState>({
    privacy: false,
    service: false,
    location: false,
    info: false,
    marketing: false,
    info_consent: false,
  });

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [phonePhase, setPhonePhase] = useState<'idle' | 'sent' | 'verified'>(
    'idle',
  );
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [emailDomain, setEmailDomain] = useState('');
  const [emailIsCustom, setEmailIsCustom] = useState(false);
  const [emailDropdown, setEmailDropdown] = useState(false);

  const emailRowRef = useRef<HTMLDivElement>(null);
  const emailDomainInputRef = useRef<HTMLInputElement>(null);

  // 이메일 표시 값: 직접입력 모드면 emailCustomDomain.value, 아니면 선택된 도메인
  const domainInputValue = emailIsCustom
    ? emailCustomDomain.value
    : emailDomain;

  const showEmailDomainError =
    (!!emailLocal.value && !emailCustomDomain.value && !emailDomain) ||
    !!emailCustomDomain.error;

  const isAllChecked = termList.every((t) => agree[t.state]);

  // 아이디 중복확인 버튼
  const handleUsernameCheck = async () => {
    if (!username.value) {
      username.setTouched(true);
      username.setError('required');
      username.setSuccess(false);
      return;
    }
    if (username.error) {
      username.setSuccess(false);
      return;
    }

    try {
      const result = await SignUpSerivce.CheckId({
        userid: username.value,
      });
      if (result.status === 'success') {
        if (!result.data.isValid) {
          username.setError('duplicate');
          username.setSuccess(false);
        } else {
          username.setError(null);
          username.setSuccess(true);
        }
      }
    } catch (error: any) {
      alert(error.response.data.message);
      console.log(error);
    }
  };

  // 휴대폰 번호 인증번호 발송 버튼
  const handleSendCode = async () => {
    if (phone.error === 'invalid') {
      alert('휴대폰 번호를 정확하게 입력해 주세요.');
      return;
    }

    if (!username.value) {
      alert('아이디를 입력해주세요');
      return;
    }

    if (!username.success) {
      alert('아이디 중복검사를 해주세요.');
      return;
    }

    try {
      const result = await SignUpSerivce.SendSms({
        type: 'S',
        loginType: 'C',
        userid: username.value,
        tel: phone.value,
      });
      if (result.status === 'success') {
        phone.setError(null);
        setPhonePhase('sent');
        setSecondsLeft(180);
        phoneCode.setValue('');
        phoneCode.setError(null);
        phoneCode.setSuccess(false);
      }
    } catch (error: any) {
      // console.log(error);
      const msg =
        error?.response?.data?.message ?? '인증번호 발송에 실패했습니다.';
      if (msg === '이미 가입된 전화번호입니다.') {
        phone.setError('duplicate');
      } else {
        if (phonePhase === 'sent') {
          phoneCode.setError(msg);
        } else {
          phone.setError(msg);
        }
      }
    }
  };

  // mm:ss 표시
  const formatMMSS = (sec: number) => {
    const m = String(Math.floor(sec / 60)).padStart(2, '0');
    const s = String(sec % 60).padStart(2, '0');
    return `${m}:${s}`;
  };

  // 인증번호 확인
  const handleVerifyCode = async () => {
    // 만료 체크
    if (secondsLeft <= 0) {
      phoneCode.setError('timeout');
      phoneCode.setSuccess(false);
      return;
    }

    if (!phoneCode.value) {
      phoneCode.setError('required');
      phoneCode.setSuccess(false);
      return;
    }

    try {
      await SignUpSerivce.CheckCode({
        type: 'S',
        loginType: 'C',
        userid: username.value,
        tel: phone.value,
        authCode: phoneCode.value,
      });

      phoneCode.setError(null);
      phoneCode.setSuccess(true);
      phone.setError(null);
      setPhonePhase('verified');
      setSecondsLeft(-1);
    } catch (error: any) {
      // alert(error.response.data.message);
      console.log(error);
      phoneCode.setError('mismatch');
      phoneCode.setSuccess(false);
    }
  };

  // 추천인코드 확인
  const handleVerifyRecommendCode = async () => {
    if (!recommendCode.value) return;
    if (localStorage.getItem('nonMemberRecommendCode')) return;

    try {
      const response = await SignUpSerivce.CheckRecommendCode(
        recommendCode.value,
      );
      const isValid = response?.data?.isValid;

      if (isValid) {
        recommendCode.setError(null);
        recommendCode.setSuccess(true);
      } else {
        recommendCode.setError('invalid');
        recommendCode.setSuccess(false);
      }
    } catch (error: any) {
      recommendCode.setError('invalid');
      recommendCode.setSuccess(false);
    }
  };

  useEffect(() => {
    const storedCode = localStorage.getItem('nonMemberRecommendCode');
    if (storedCode) {
      recommendCode.setValue(storedCode);
      recommendCode.setSuccess(true);
      recommendCode.setError(null);
    }
  }, []);

  const closeDropdown = () => setEmailDropdown(false);
  // 드롭다운 도메인 선택
  const handleDomainSelect = (domain: string) => {
    if (domain === '직접입력') {
      setEmailIsCustom(true);
      setEmailDomain('');
      closeDropdown();
      // 직접입력 전환 시 인풋 포커스
      setTimeout(() => emailDomainInputRef.current?.focus(), 0);
    } else {
      setEmailIsCustom(false);
      setEmailDomain(domain);
      // 커스텀 값은 초기화(선택 도메인이 보이도록)
      emailCustomDomain.setValue('');
      closeDropdown();
    }
  };

  // 바깥 클릭 시 드롭다운 닫기
  useEffect(() => {
    if (!emailDropdown) return;
    const onDocClick = (e: MouseEvent) => {
      if (!emailRowRef.current?.contains(e.target as Node)) {
        closeDropdown();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [emailDropdown]);

  const passwordMatch = useMemo(
    () =>
      !!password.value &&
      !!passwordConfirm.value &&
      password.value === passwordConfirm.value,
    [password.value, passwordConfirm.value],
  );

  const usernameFeedback =
    username.error === 'required'
      ? feedbackMessages.username.required
      : username.error === 'duplicate'
        ? feedbackMessages.username.duplicate
        : username.success
          ? feedbackMessages.username.available
          : feedbackMessages.username.default;

  const passwordFeedback =
    password.error === 'required'
      ? feedbackMessages.password.required
      : feedbackMessages.password.default;

  const passwordConfirmFeedback =
    passwordConfirm.error === 'required'
      ? feedbackMessages.passwordConfirm.required
      : passwordMatch
        ? feedbackMessages.passwordConfirm.success
        : feedbackMessages.passwordConfirm.mismatch;

  const nameFeedback =
    name.error === 'required'
      ? feedbackMessages.name.required
      : name.error === 'invalid'
        ? feedbackMessages.name.invalid
        : feedbackMessages.name.default;

  const phoneFeedback =
    phone.error === 'required'
      ? feedbackMessages.phone.required
      : phone.error === 'invalid'
        ? feedbackMessages.phone.invalid
        : phone.error === 'duplicate'
          ? feedbackMessages.phone.duplicate
          : phone.error || feedbackMessages.phone.default;

  const phoneCodeFeedback =
    phoneCode.error === 'expired' || secondsLeft === 0
      ? feedbackMessages.phoneCode.timeout
      : phoneCode.error === 'required'
        ? feedbackMessages.phoneCode.required
        : phoneCode.error === 'mismatch'
          ? feedbackMessages.phoneCode.mismatch
          : phoneCode.error || feedbackMessages.phoneCode.default;

  // 3분 타이머
  useEffect(() => {
    if (phonePhase !== 'sent') return;
    if (secondsLeft <= 0) return;
    const t = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [phonePhase, secondsLeft]);

  const isUsernameOk = username.success;
  const isPasswordOk = !!password.value && !password.error;
  const isPasswordConfirmOk = passwordMatch;
  const isNameOk = !!name.value && !name.error;
  const isPhoneOk = phoneCode.success;

  // 이메일 조건: 미입력 이거나, 선택형에서 도메인 선택했거나, 직접입력에서 도메인 유효
  const isEmailOk =
    !emailLocal.value ||
    (!emailIsCustom && !!emailDomain) ||
    (emailIsCustom && !!emailCustomDomain.value && !emailCustomDomain.error);

  // 추천인 코드(선택) OK 조건: 미입력 이거나, 입력 시 검증 성공
  const isRecommendOk = !recommendCode.value || recommendCode.success;

  //  필수 약관 동의
  const requiredTermsOk = termList
    .filter((t) => t.required)
    .every((t) => agree[t.state]);

  const avaliableSignup =
    isUsernameOk &&
    isPasswordOk &&
    isPasswordConfirmOk &&
    isNameOk &&
    isPhoneOk &&
    isEmailOk &&
    isRecommendOk &&
    requiredTermsOk;

  const SignUpApi = async () => {
    const local = emailLocal.value.trim();
    const domain = (domainInputValue ?? '').trim().replace(/^@+/, '');
    const email = local && domain ? `${local}@${domain}` : '';
    try {
      const result = await SignUpSerivce.signUp({
        usertype: userType === 'company' ? 'B' : 'I',
        userid: username.value,
        department: department.value,
        position: position.value,
        name: name.value,
        email: email,
        tel: phone.value,
        password: password.value,
        notification: agree.info ? 'Y' : 'N',
        marketing: agree.marketing ? 'Y' : 'N',
        info_consent: agree.info_consent ? 'Y' : 'N',
        loginType: 'C',
        code:
          typeof recommendCode.value === 'string'
            ? recommendCode.value
            : String(recommendCode.value),
      });
      if (result.status === 'success') {
        localStorage.removeItem('socialLoginType');
        navigate('/signUpComplete');
      }
    } catch (error: any) {
      alert(error.response.data.message);
      console.log(error);
    }
  };

  const handleSignup = (e: React.FormEvent) => {
    e.preventDefault();
    if (!avaliableSignup) return;
    // 제출 로직…
    sessionStorage.setItem('name', name.value);
    SignUpApi();
  };

  return (
    <PageContainer>
      <PageTop
        text={userType === 'company' ? '채권보유사 회원가입' : '회원가입'}
        isMenu={false}
        hasSearch={false}
      />
      <S.Content>
        <S.Form onSubmit={handleSignup} autoComplete="off">
          <S.Inner>
            {userType === 'company' && (
              <>
                <S.Section>
                  <S.SectionTitle>소속</S.SectionTitle>
                  <S.SectionContent>
                    <S.InputGroup>
                      <S.TextInput
                        type="text"
                        placeholder="개인 또는 소속된 상호(법인)명을 입력해주세요."
                        value={department.value}
                        onChange={(e) => department.onChange(e.target.value)}
                      />
                    </S.InputGroup>
                  </S.SectionContent>
                </S.Section>

                <S.Section>
                  <S.SectionTitle>직함</S.SectionTitle>
                  <S.SectionContent>
                    <S.InputGroup>
                      <S.TextInput
                        type="text"
                        placeholder="직함을 입력해주세요."
                        value={position.value}
                        onChange={(e) => position.onChange(e.target.value)}
                      />
                    </S.InputGroup>
                  </S.SectionContent>
                </S.Section>
              </>
            )}

            <S.Section>
              <S.SectionTitle>아이디</S.SectionTitle>
              <S.SectionContent>
                <S.InputGroup>
                  <S.TextInput
                    name="authId"
                    autoComplete="off"
                    autoCapitalize="off"
                    type="text"
                    placeholder="아이디를 입력해 주세요."
                    value={username.value}
                    onChange={(e) => username.onChange(e.target.value)}
                    onBlur={username.onBlur}
                    onFocus={() => username.setTouched(true)}
                    $error={!!username.error}
                    $success={username.success}
                  />
                  <S.InputConfirmButton
                    $disabled={
                      !username.value || !!username.error || username.success
                    }
                    onClick={handleUsernameCheck}
                  >
                    {username.success ? '확인완료' : '중복확인'}
                  </S.InputConfirmButton>
                </S.InputGroup>
                <S.FeedbackArea>
                  <S.IconWrapper>
                    {username.success ? (
                      <img
                        src="/assets/images/check/check-circle-blue.svg"
                        alt="available"
                      />
                    ) : !!username.error ? (
                      <img
                        src="/assets/images/icon/error-red.svg"
                        alt="error"
                      />
                    ) : (
                      <img
                        src="/assets/images/icon/info_16.svg"
                        alt="default"
                      />
                    )}
                  </S.IconWrapper>
                  <S.Feedback
                    $error={!!username.error}
                    $success={username.success}
                  >
                    {usernameFeedback}
                  </S.Feedback>
                </S.FeedbackArea>
              </S.SectionContent>
            </S.Section>
            <S.Section>
              <S.SectionTitle>
                비밀번호
                <img
                  src={`/assets/images/icon/${passwordVisible ? 'visible' : 'notVisible_icon'}.svg`}
                  alt=""
                  onClick={() => setPasswordVisible((prev) => !prev)}
                />
              </S.SectionTitle>
              <S.SectionContent>
                <S.InputGroup>
                  <S.TextInput
                    name="authPwView"
                    autoComplete="new-password"
                    type={passwordVisible ? 'text' : 'password'}
                    placeholder="비밀번호를 입력해 주세요."
                    value={password.value}
                    onChange={(e) => password.onChange(e.target.value)}
                    onBlur={password.onBlur}
                    onFocus={() => password.setTouched(true)}
                    $error={!!password.error}
                  />
                  {password.value && !password.error && (
                    <S.PasswordSuccessIcon
                      src="/assets/images/check/circle-checked-blue.svg"
                      alt=""
                    />
                  )}
                </S.InputGroup>
                <S.FeedbackArea>
                  <S.IconWrapper>
                    {!!password.error ? (
                      <img
                        src="/assets/images/icon/error-red.svg"
                        alt="error"
                      />
                    ) : (
                      <img
                        src="/assets/images/icon/info_16.svg"
                        alt="default"
                      />
                    )}
                  </S.IconWrapper>
                  <S.Feedback $error={!!password.error}>
                    {passwordFeedback}
                  </S.Feedback>
                </S.FeedbackArea>
              </S.SectionContent>
            </S.Section>
            <S.Section>
              <S.SectionTitle>비밀번호 확인</S.SectionTitle>
              <S.SectionContent>
                <S.InputGroup>
                  <S.TextInput
                    type={passwordVisible ? 'text' : 'password'}
                    placeholder="비밀번호 재입력"
                    value={passwordConfirm.value}
                    onChange={(e) => passwordConfirm.onChange(e.target.value)}
                    onBlur={passwordConfirm.onBlur}
                    onFocus={() => passwordConfirm.setTouched(true)}
                    $error={
                      passwordConfirm.touched &&
                      !!password.value &&
                      !password.error &&
                      !passwordMatch
                    }
                  />
                  {!password.error && passwordMatch && (
                    <S.PasswordSuccessIcon
                      src="/assets/images/check/circle-checked-blue.svg"
                      alt=""
                    />
                  )}
                </S.InputGroup>
                {passwordConfirm.touched &&
                  password.value &&
                  !password.error && (
                    <S.FeedbackArea>
                      <S.IconWrapper>
                        {passwordMatch && (
                          <img
                            src="/assets/images/check/check-circle-blue.svg"
                            alt="success"
                          />
                        )}
                        {(!!passwordConfirm.error || !passwordMatch) && (
                          <img
                            src="/assets/images/icon/error-red.svg"
                            alt="error"
                          />
                        )}
                      </S.IconWrapper>
                      <S.Feedback
                        $error={!!passwordConfirm.error || !passwordMatch}
                        $success={passwordMatch}
                      >
                        {passwordConfirmFeedback}
                      </S.Feedback>
                    </S.FeedbackArea>
                  )}
              </S.SectionContent>
            </S.Section>
            <S.Section>
              <S.SectionTitle>이름</S.SectionTitle>
              <S.SectionContent>
                <S.InputGroup>
                  <S.TextInput
                    type="text"
                    placeholder="실명을 입력해 주세요."
                    value={name.value}
                    onChange={(e) => name.onChange(e.target.value)}
                    onBlur={name.onBlur}
                    onFocus={() => name.setTouched(true)}
                    $error={!!name.error}
                  />
                </S.InputGroup>
                {!!name.error && (
                  <S.FeedbackArea>
                    <S.IconWrapper>
                      <img
                        src="/assets/images/icon/error-red.svg"
                        alt="error"
                      />
                    </S.IconWrapper>
                    <S.Feedback $error>{nameFeedback}</S.Feedback>
                  </S.FeedbackArea>
                )}
              </S.SectionContent>
            </S.Section>
            <S.Section>
              <S.SectionTitle>휴대폰 번호</S.SectionTitle>
              <S.SectionContent>
                <S.InputGroup>
                  <S.TextInput
                    type="tel"
                    placeholder="숫자만 입력해 주세요."
                    value={phone.value}
                    onChange={(e) => phone.onChange(e.target.value)}
                    onBlur={phone.onBlur}
                    onFocus={() => phone.setTouched(true)}
                    $error={!!phone.error}
                    $success={phoneCode.success}
                    disabled={phoneCode.success}
                  />
                  {phonePhase === 'verified' ? null : (
                    <S.InputConfirmButton
                      type="button"
                      $disabled={!phone.value && phonePhase !== 'sent'}
                      $isRecent={phonePhase === 'sent'}
                      onClick={handleSendCode}
                    >
                      {phonePhase === 'sent' ? '재발송' : '인증번호 발송'}
                    </S.InputConfirmButton>
                  )}
                </S.InputGroup>
                {phonePhase === 'idle' && !!phone.error && (
                  <S.FeedbackArea>
                    <S.IconWrapper>
                      <img
                        src="/assets/images/icon/error-red.svg"
                        alt="error"
                      />
                    </S.IconWrapper>
                    <S.Feedback $error>{phoneFeedback}</S.Feedback>
                  </S.FeedbackArea>
                )}
              </S.SectionContent>
              {phonePhase !== 'idle' && (
                <S.SectionContent>
                  <S.InputGroup>
                    <S.TextInput
                      type="tel"
                      placeholder="인증번호 6자리를 입력해 주세요."
                      value={phoneCode.value}
                      onChange={(e) => phoneCode.onChange(e.target.value)}
                      onBlur={phoneCode.onBlur}
                      onFocus={() => phoneCode.setTouched(true)}
                      $error={!!phoneCode.error}
                      $success={phoneCode.success}
                      disabled={phoneCode.success}
                    />
                    <S.InputConfirmButton
                      type="button"
                      $disabled={phonePhase === 'verified' || secondsLeft === 0}
                      onClick={handleVerifyCode}
                    >
                      {phonePhase === 'verified' ? '확인완료' : '인증번호 확인'}
                    </S.InputConfirmButton>
                  </S.InputGroup>
                  {phonePhase !== 'verified' && (
                    <S.FeedbackArea>
                      <S.TimeLeftLabel>입력대기시간 :</S.TimeLeftLabel>
                      <S.Feedback $error>
                        {formatMMSS(Math.max(0, secondsLeft))}
                      </S.Feedback>
                    </S.FeedbackArea>
                  )}
                  {(!!phoneCode.error || secondsLeft === 0) && (
                    <S.FeedbackArea>
                      <S.IconWrapper>
                        <img
                          src="/assets/images/icon/error-red.svg"
                          alt="error"
                        />
                      </S.IconWrapper>
                      <S.Feedback $error>{phoneCodeFeedback}</S.Feedback>
                    </S.FeedbackArea>
                  )}
                </S.SectionContent>
              )}
            </S.Section>
            <S.Section>
              <S.SectionTitleOptional>
                <S.SectionTitle>이메일</S.SectionTitle>
                (선택)
              </S.SectionTitleOptional>
              <S.SectionContent>
                <S.InputGroup ref={emailRowRef}>
                  <S.TextInput
                    type="text"
                    placeholder="이메일"
                    value={emailLocal.value}
                    onChange={(e) => emailLocal.onChange(e.target.value)}
                  />
                  <Email>@</Email>
                  <EmailDomainSelect
                    $isCustom={emailIsCustom}
                    $error={showEmailDomainError}
                    onClick={() => {
                      if (!emailIsCustom) {
                        setEmailDropdown((prev) => !prev);
                      }
                    }}
                  >
                    <EmailDomainInput
                      ref={emailDomainInputRef}
                      placeholder={
                        emailIsCustom
                          ? '도메인을 입력해 주세요.'
                          : '선택해 주세요.'
                      }
                      value={domainInputValue}
                      onChange={(e) => {
                        if (emailIsCustom) {
                          emailCustomDomain.onChange(e.target.value);
                        }
                      }}
                      readOnly={!emailIsCustom}
                      onClick={(e) => {
                        if (!emailIsCustom) {
                          e.stopPropagation();
                          setEmailDropdown((prev) => !prev);
                        }
                      }}
                      $clickable={!emailIsCustom}
                    />
                    <ArrowButton
                      src="/assets/images/arrow/arrow-down-6B7684.svg"
                      alt=""
                      onClick={(e) => {
                        e.stopPropagation();
                        setEmailDropdown((prev) => !prev);
                      }}
                      aria-expanded={emailDropdown}
                    />
                  </EmailDomainSelect>
                  {emailDropdown && (
                    <Dropdown>
                      {emailList.map((domain) => (
                        <DropdownItem
                          key={domain}
                          onClick={() => handleDomainSelect(domain)}
                        >
                          {domain}
                        </DropdownItem>
                      ))}
                    </Dropdown>
                  )}
                </S.InputGroup>
                {showEmailDomainError && (
                  <S.FeedbackArea>
                    <S.IconWrapper>
                      <img
                        src="/assets/images/icon/error-red.svg"
                        alt="error"
                      />
                    </S.IconWrapper>
                    <S.Feedback $error>
                      올바른 이메일 형식이 아닙니다.
                    </S.Feedback>
                  </S.FeedbackArea>
                )}
              </S.SectionContent>
            </S.Section>
            <S.Section>
              <S.SectionTitleOptional>
                <S.SectionTitle>추천인 코드</S.SectionTitle>
                (선택)
              </S.SectionTitleOptional>
              <S.SectionContent>
                <S.InputGroup>
                  <S.TextInput
                    type="text"
                    placeholder="추천인 코드를 입력해 주세요."
                    value={recommendCode.value}
                    onChange={(e) => recommendCode.onChange(e.target.value)}
                    onBlur={recommendCode.onBlur}
                    onFocus={() => recommendCode.setTouched(true)}
                    $error={!!recommendCode.error}
                    $success={recommendCode.success}
                    disabled={recommendCode.success}
                    readOnly={!!localStorage.getItem('nonMemberRecommendCode')} // readOnly 적용
                  />
                  <S.InputConfirmButton
                    $disabled={recommendCode.success || !recommendCode.value}
                    onClick={handleVerifyRecommendCode}
                  >
                    {recommendCode.success ? '확인완료' : '확인'}
                  </S.InputConfirmButton>
                </S.InputGroup>
                {!!recommendCode.error && (
                  <S.FeedbackArea>
                    <S.IconWrapper>
                      <img
                        src="/assets/images/icon/error-red.svg"
                        alt="error"
                      />
                    </S.IconWrapper>
                    <S.Feedback $error>
                      존재하지 않는 추천인 코드입니다.
                    </S.Feedback>
                  </S.FeedbackArea>
                )}
              </S.SectionContent>
            </S.Section>
            <S.Section>
              <S.SectionTitle>약관동의</S.SectionTitle>
              <S.TermsBox>
                <S.TermsRow
                  onClick={() => {
                    if (isAllChecked) {
                      setAgree({
                        privacy: false,
                        service: false,
                        location: false,
                        info: false,
                        marketing: false,
                        info_consent: false,
                      });
                      return;
                    }
                    setAgree({
                      privacy: true,
                      service: true,
                      location: true,
                      info: true,
                      marketing: true,
                      info_consent: true,
                    });
                  }}
                >
                  <S.AllAgreeCheckbox $checked={isAllChecked}>
                    {isAllChecked && (
                      <img
                        src="/assets/images/check/check-blue-on.svg"
                        alt="checked"
                      />
                    )}
                  </S.AllAgreeCheckbox>
                  <S.AllAgree>전체 동의</S.AllAgree>
                  <S.TermsValue>(선택사항 포함)</S.TermsValue>
                </S.TermsRow>
                <S.Divider />
                <S.TermsList>
                  {termList.map((item) => {
                    const key = item.state;
                    return (
                      <S.TermsRow
                        key={item.state}
                        onClick={() => {
                          setAgree({
                            ...agree,
                            [key]: !agree[key],
                          });
                        }}
                      >
                        {agree[key] ? (
                          <S.Checkbox>
                            <img
                              src="/assets/images/check/check-blue-on.svg"
                              alt="checked"
                            />
                          </S.Checkbox>
                        ) : (
                          <S.Checkbox />
                        )}
                        <S.TermsContent>
                          <S.TermsValue>
                            {item.required ? <span>[필수]</span> : '[선택]'}{' '}
                            {item.name}
                          </S.TermsValue>
                          <S.SeeMoreButton
                            src="/assets/images/arrow/arrow-right-gray-small.svg"
                            alt=""
                            onClick={(
                              e: React.MouseEvent<HTMLElement, MouseEvent>,
                            ) => {
                              e.stopPropagation();
                              window.open(item.url, '_blank');
                            }}
                          />
                        </S.TermsContent>
                      </S.TermsRow>
                    );
                  })}
                </S.TermsList>
              </S.TermsBox>
            </S.Section>
          </S.Inner>
          <S.SignupButton $disabled={!avaliableSignup} type="submit">
            가입하기
          </S.SignupButton>
        </S.Form>
        <S.Login>
          이미 땅집고옥션 회원 이신가요?
          <S.LoginButton onClick={() => navigate('/login')}>
            로그인
          </S.LoginButton>
        </S.Login>
      </S.Content>
    </PageContainer>
  );
}

export default SignUpForm;

const Email = styled.div`
  color: #6b7684;
  font-size: 16px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.32px;
`;

const EmailDomainSelect = styled.div<{
  $error?: boolean;
  $isCustom?: boolean;
}>`
  display: flex;
  flex-direction: row;
  height: 50px;
  padding: 12px 16px;
  align-items: center;
  gap: 6px;
  flex: 1 0 0;
  min-width: 0;
  border-radius: 8px;
  border: 1px solid #e5e8ea;
  background: #f7f8fc;
  cursor: ${({ $isCustom }) => !$isCustom && 'pointer'};

  ${({ $error }) =>
    $error &&
    css`
      border-color: #f04452;
    `}
`;

const EmailDomainInput = styled.input<{ $clickable?: boolean }>`
  min-width: 0;
  flex: 1 0 0;

  color: rgba(0, 12, 30, 0.8);
  font-size: 16px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.32px;
  background-color: transparent;
  border: none;
  outline: none;

  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'text')};

  &::placeholder {
    color: #6b7684;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  z-index: 10;
  top: 58px;
  right: 0;

  display: flex;
  width: 184px;
  padding: 6px;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 12px;
  border: 1px solid #e1e2e4;
  background: #fff;
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.12);
`;

const DropdownItem = styled.div`
  display: flex;
  padding: 6px 6px 6px 8px;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  border-radius: 8px;
  cursor: pointer;

  color: rgba(0, 12, 30, 0.8);
  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  &:active {
    background: rgba(0, 0, 0, 0.09);
  }
`;

const ArrowButton = styled.img`
  cursor: pointer;
`;
